// SP-2b — hand-rolled first-run tour: a dismissible, NON-blocking inline
// step-through rendered as a Card banner at the top of a page (never an
// overlay/portal, never traps focus). Hidden while the ui-flags read is
// loading and forever once the tour's flag is stamped; finishing the last
// step or skipping stamps the flag via POST /api/me/ui-flags/{key}.

import { useState, type ReactNode } from "react";
import { Button, Card } from "@/design-system/components/ui";
import { useStampUiFlag, useUiFlags } from "@/hooks/uiFlags/useUiFlags";

export interface FirstRunTourStep {
  title: string;
  body: ReactNode;
}

export interface FirstRunTourProps {
  tourKey: string;
  steps: FirstRunTourStep[];
  /** Extra classes for the banner Card (spacing at the mount site). */
  className?: string;
}

export function FirstRunTour({ tourKey, steps, className }: FirstRunTourProps) {
  const flags = useUiFlags();
  const stamp = useStampUiFlag();
  const [stepIndex, setStepIndex] = useState(0);
  // Local dismissal hides the banner immediately and guarantees the stamp
  // mutation fires exactly once, whatever the invalidated refetch returns.
  const [dismissed, setDismissed] = useState(false);

  if (
    dismissed ||
    steps.length === 0 ||
    flags.isLoading ||
    !flags.data ||
    flags.data.includes(tourKey)
  ) {
    return null;
  }

  const step = steps[Math.min(stepIndex, steps.length - 1)];
  const isLast = stepIndex >= steps.length - 1;

  const finish = () => {
    setDismissed(true);
    stamp.mutate(tourKey);
  };

  return (
    <Card data-testid={`first-run-tour-${tourKey}`} role="note" className={className}>
      <div className="flex items-start justify-between gap-space-3">
        <div>
          <p className="font-display text-caption uppercase tracking-label text-gray-mid m-0">
            Quick tour — step {stepIndex + 1} of {steps.length}
          </p>
          <h4
            className="font-display font-bold text-h4 text-onyx m-0 mt-space-1"
            data-testid="tour-step-title"
          >
            {step.title}
          </h4>
          <div className="text-small text-onyx mt-space-1" data-testid="tour-step-body">
            {step.body}
          </div>
        </div>
        <Button variant="text" size="sm" onClick={finish} data-testid="tour-skip">
          Skip tour
        </Button>
      </div>
      <div className="flex gap-space-2 mt-space-3">
        {stepIndex > 0 && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
            data-testid="tour-back"
          >
            Back
          </Button>
        )}
        <Button
          variant="primary"
          size="sm"
          onClick={() => (isLast ? finish() : setStepIndex((i) => i + 1))}
          data-testid="tour-next"
        >
          {isLast ? "Done" : "Next"}
        </Button>
      </div>
    </Card>
  );
}
